export type CongregationRole = 'DEVOTEE' | 'MENTOR' | 'ADMIN' | string;

export interface CongregationRoleAssignment {
  id: number;
  memberId: number;
  role: CongregationRole;
  assignedAt?: string;
  revokedAt?: string | null;
}

export type CongregationMemberStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'SUSPENDED'
  | string;

export type CongregationInitiatedWith = 'NO' | 'HARINAM' | 'BRAHMIN';

export interface CongregationMember {
  id: number;
  userId: number;
  displayName: string;
  photoUrl?: string | null;
  legalName?: string | null;
  phone?: string | null;
  email?: string | null;
  pan?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  maritalStatus?: string | null;
  occupation?: string | null;
  education?: string | null;
  spiritualMasterId?: number | null;
  initiatedWith?: CongregationInitiatedWith;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  whatsappOptIn?: boolean;
  about?: string | null;
  status: CongregationMemberStatus;
  createdAt?: string;
  updatedAt?: string;
  mentorMemberId?: number | null;
  mentorDisplayName?: string | null;
  centerId?: number | null;
  centerName?: string | null;
}

export interface CongregationMeResponse {
  member?: CongregationMember | null;
  roles: CongregationRoleAssignment[];
}

export interface CongregationMasterCenter {
  id: number;
  name: string;
  city?: string | null;
  state?: string | null;
}

export interface CongregationSpiritualGuide {
  id: number;
  memberId: number;
  memberDisplayName: string;
  memberPhotoUrl?: string | null;
  bio?: string | null;
  isActive: boolean;
  capacity?: number | null;
  centerId?: number | null;
  centerName?: string | null;
}

export interface CongregationSpiritualMaster {
  id: number;
  name: string;
  isActive: boolean;
  centerId?: number | null;
  centerName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationCourseMaster {
  id: number;
  name: string;
  orderIndex?: number | null;
  description?: string | null;
  isRequired?: boolean;
}

// Alias used by ported admin pages
export type CongregationCourse = CongregationCourseMaster;

export type CongregationBatchStatus = 'PLANNED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export interface CongregationBatch {
  id: number;
  name: string;
  mentorId: number;
  mentorName?: string;
  courseId: number;
  courseName?: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status: CongregationBatchStatus;
  maxCapacity?: number | null;
  enrolledCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationBatchInput {
  name: string;
  courseId: number;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  maxCapacity?: number | null;
  mentorId?: number | null;
}

export type CongregationBatchEnrollmentStatus = 'ENROLLED' | 'COMPLETED' | 'DROPPED' | 'TRANSFERRED';

export interface CongregationBatchEnrollment {
  id: number;
  batchId: number;
  batchName?: string;
  memberId: number;
  memberName?: string;
  enrolledBy: number;
  enrolledByName?: string;
  status: CongregationBatchEnrollmentStatus;
  enrolledAt: string;
  completedAt?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationBatchEnrollmentInput {
  memberId: number;
  notes?: string | null;
}

export interface CongregationFacilitator {
  id: number;
  memberId: number;
  memberDisplayName: string;
  memberPhotoUrl?: string | null;
  batchId: number;
  batchName: string;
  bio?: string | null;
  isActive: boolean;
  roleAssignmentId: number;
  createdAt: string;
  updatedAt: string;
}

// Batch Posts Types
export type BatchPostReactionType = 'like' | 'heart' | 'celebrate' | 'insightful' | 'haribol';

export interface CongregationBatchPost {
  id: number;
  batchId: number;
  authorMemberId: number;
  authorName?: string;
  authorPhotoUrl?: string;
  content: string;
  isPinned: boolean;
  reactionCounts?: Record<BatchPostReactionType, number>;
  repliesCount: number;
  myReaction?: BatchPostReactionType | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBatchPostInput {
  content: string;
  isPinned?: boolean;
  attachments?: CongregationBatchPostAttachmentInput[];
  poll?: CongregationBatchPostPollInput;
}

export interface CongregationBatchPostReaction {
  id: number;
  postId: number;
  memberId: number;
  memberName?: string;
  reactionType: BatchPostReactionType;
  createdAt: string;
}

export interface CongregationBatchPostReply {
  id: number;
  postId: number;
  parentReplyId?: number | null;
  authorMemberId: number;
  authorName?: string;
  authorPhotoUrl?: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBatchPostReplyInput {
  content: string;
  parentReplyId?: number | null;
}

export type BatchPostAttachmentType = 'image' | 'file' | 'link';

export interface CongregationBatchPostAttachment {
  id: number;
  postId: number;
  attachmentType: BatchPostAttachmentType;
  url: string;
  filename?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  metadata?: Record<string, any> | null;
  createdAt: string;
}

export interface CongregationBatchPostAttachmentInput {
  attachmentType: BatchPostAttachmentType;
  url: string;
  filename?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  metadata?: Record<string, any> | null;
}

export interface CongregationBatchPostPoll {
  id: number;
  postId: number;
  question: string;
  expiresAt?: string | null;
  hasVoted?: boolean;
  options: CongregationBatchPostPollOption[];
  createdAt: string;
}

export interface CongregationBatchPostPollInput {
  question: string;
  expiresAt?: string | null;
  options: string[];
}

export interface CongregationBatchPostPollOption {
  id: number;
  pollId: number;
  optionText: string;
  votesCount: number;
}

export interface CongregationBatchPostPollVote {
  id: number;
  pollId: number;
  optionId: number;
  memberId: number;
  createdAt: string;
}

export interface CongregationBatchPostWithDetails {
  id: number;
  batchId: number;
  authorMemberId: number;
  authorName?: string;
  authorPhotoUrl?: string;
  content: string;
  isPinned: boolean;
  reactionCounts?: Record<BatchPostReactionType, number>;
  repliesCount: number;
  myReaction?: BatchPostReactionType | null;
  attachments: CongregationBatchPostAttachment[];
  poll?: CongregationBatchPostPoll | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBookMaster {
  id: number;
  title: string;
  author?: string | null;
  description?: string | null;
  orderIndex?: number | null;
  isPrimary?: boolean;
}

// Alias used by ported admin pages
export type CongregationBook = CongregationBookMaster;

// Book Reading Tracking Types
export type BookReadingStatus = 'READING' | 'COMPLETED' | 'ABANDONED';

export interface CongregationBookReading {
  id: number;
  memberId: number;
  memberName?: string;
  bookId: number;
  bookTitle?: string;
  bookAuthor?: string | null;
  status: BookReadingStatus;
  startDate: string; // ISO 8601 string in IST
  finishDate?: string | null; // ISO 8601 string in IST
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBookReadingInput {
  bookId: number;
  status: BookReadingStatus;
  startDate: string; // ISO 8601 string in IST
  finishDate?: string | null; // ISO 8601 string in IST
  notes?: string | null;
}

export type BookRecommendationTargetType = 'BATCH' | 'MEMBER';

export interface CongregationBookRecommendation {
  id: number;
  bookId: number;
  bookTitle?: string;
  bookAuthor?: string | null;
  recommendedBy: number;
  recommendedByName?: string;
  targetType: BookRecommendationTargetType;
  targetId: number; // batchId or memberId
  targetName?: string; // batch or member name
  message?: string | null;
  priority?: number | null; // 1-5, higher = more important
  createdAt: string;
}

export interface CongregationBookRecommendationInput {
  bookId: number;
  targetType: BookRecommendationTargetType;
  targetId: number;
  message?: string | null;
  priority?: number | null;
}

export interface CongregationSkillMaster {
  id: number;
  name: string;
  description?: string | null;
}

export interface CongregationEligibilityConfig {
  harinamRounds?: number | null;
  brahminRounds?: number | null;
  requiredCourses?: number | null;
  requiredDocuments?: number | null;
  effectiveDate?: string | null;
}

export interface CongregationMastersPayload {
  centers: CongregationMasterCenter[];
  spiritualGuides: CongregationSpiritualGuide[];
  spiritualMasters: CongregationSpiritualMaster[];
  courses: CongregationCourseMaster[];
  books: CongregationBookMaster[];
  skills: CongregationSkillMaster[];
  eligibility?: CongregationEligibilityConfig | null;
}

export type CongregationMentorNotificationType =
  | 'APPLICATION_DECIDED'
  | 'DOCUMENT_REVIEW_REQUEST'
  | 'FAMILY_LINK_CONFIRMED'
  | 'FOLLOW_UP_REMINDER'
  | string;

export interface CongregationMentorNotification {
  id: number | string;
  type: CongregationMentorNotificationType;
  title?: string | null;
  message?: string | null;
  createdAt: string;
  readAt?: string | null;
  actorName?: string | null;
  memberId?: number | null;
  memberName?: string | null;
  applicationId?: number | null;
  applicationName?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CongregationMentorNotificationsResponse {
  notifications: CongregationMentorNotification[];
}

export interface CongregationMentorAuditEvent {
  id: number | string;
  action: string;
  description?: string | null;
  createdAt: string;
  entityType?: string | null;
  entityId?: number | string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CongregationMentorAuditEventsResponse {
  events: CongregationMentorAuditEvent[];
}

export type SadhanaTemplateUnit = 'MARKS' | 'MINUTES' | 'BOOLEAN' | 'ROUNDS' | 'TIME' | 'UNKNOWN' | string;

export type SadhanaTemplateFrequency = 'DAILY' | 'WEEKLY' | string;

export interface SadhanaTemplateItem {
  id: number;
  templateId: number;
  section: string;
  title: string;
  description?: string | null;
  thresholds?: Record<string, unknown> | null;
  unit: SadhanaTemplateUnit;
  frequency: SadhanaTemplateFrequency;
  ruleJson?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SadhanaTemplateDefaultAssignmentScope = 'ASSIGNED_DEVOTEES' | 'SPECIFIC_DEVOTEES';

export interface SadhanaTemplateAssignmentFailure {
  devoteeMemberId: number;
  error: string;
}

export interface SadhanaTemplate {
  id: number;
  name: string;
  description?: string | null;
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt: string;
  updatedAt: string;
  items: SadhanaTemplateItem[];
}

export interface SadhanaTemplateListResponse {
  templates: SadhanaTemplate[];
}

export type SadhanaTemplateApplyStrategy = 'IMMEDIATE' | 'NEXT_SUNDAY' | string;

export interface SadhanaTemplateCreateItemInput {
  section: string;
  title: string;
  description?: string | null;
  thresholds?: Record<string, unknown> | null;
  unit: SadhanaTemplateUnit;
  frequency: SadhanaTemplateFrequency;
  ruleJson?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  sortOrder?: number | null;
}

export interface SadhanaTemplateDefaultAssignmentRequest {
  scope: SadhanaTemplateDefaultAssignmentScope;
  devoteeMemberIds?: number[] | null;
  applyStrategy?: SadhanaTemplateApplyStrategy | null;
  effectiveFrom?: string | null;
  reason?: string | null;
}

export interface SadhanaTemplateDefaultAssignmentsResult {
  created: SadhanaTemplateAssignment[];
  failures: SadhanaTemplateAssignmentFailure[];
  skippedReason?: string | null;
}

export interface SadhanaTemplateCreateRequest {
  name: string;
  description?: string | null;
  items: SadhanaTemplateCreateItemInput[];
  defaultAssignment?: SadhanaTemplateDefaultAssignmentRequest | null;
}

export interface SadhanaTemplateCreateResponse {
  template: SadhanaTemplate;
  defaultAssignments?: SadhanaTemplateDefaultAssignmentsResult | null;
}

export interface SadhanaTemplateUpdateItemInput extends SadhanaTemplateCreateItemInput {
  id?: number | null;
  isActive?: boolean | null;
}

export interface SadhanaTemplateUpdateRequest {
  name: string;
  description?: string | null;
  items?: SadhanaTemplateUpdateItemInput[] | null;
}

export interface SadhanaTemplateUpdateResponse {
  template: SadhanaTemplate;
}

export interface SadhanaTemplateDeleteResponse {
  template: SadhanaTemplate;
}

export interface SadhanaTemplateAssignment {
  id: number;
  devoteeMemberId: number;
  templateId: number;
  mentorMemberId?: number | null;
  applyStrategy: SadhanaTemplateApplyStrategy;
  effectiveFrom: string;
  reason?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SadhanaTemplateAssignmentResponse {
  template: SadhanaTemplate;
  assignment: SadhanaTemplateAssignment | null;
}

export interface SadhanaTemplateAssignmentsResponse {
  assignments: SadhanaTemplateAssignment[];
}

export interface SadhanaTemplateAssignmentRequest {
  templateId: number;
  applyStrategy: SadhanaTemplateApplyStrategy;
  effectiveFrom?: string | null;
  reason?: string | null;
}

export interface SadhanaTemplateAssignDefaultsResponse {
  template: SadhanaTemplate;
  created: SadhanaTemplateAssignment[];
  failures: SadhanaTemplateAssignmentFailure[];
  skippedReason?: string | null;
}

export type SadhanaWeekStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'LOCKED' | string;

export interface SadhanaCardWeek {
  id: number;
  devoteeMemberId: number;
  templateId: number;
  mentorAssignmentId?: number | null;
  weekStartDate: string;
  weekEndDate: string;
  status: SadhanaWeekStatus;
  prescribedRoundsSnapshot: number;
  roundsCompletedTotal: number;
  roundsComplianceDays: number;
  mentorComment?: string | null;
  mentorCommentedAt?: string | null;
  mentorCommentedByMemberId?: number | null;
  mentorCommentedByUserId?: number | null;
  lockedAt?: string | null;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SadhanaWeeksResponse {
  weeks: SadhanaCardWeek[];
  hasMore: boolean;
  nextBefore?: string | null;
}

export interface SadhanaEntry {
  id: number;
  weekId: number;
  itemId: number;
  entryDate: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueMinutes?: number | null;
  valueBool?: boolean | null;
  valueTime?: string | null;
  valueRounds?: number | null;
  completedTime?: string | null;
  marksAwarded: number;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SadhanaWeekDetailResponse {
  week: SadhanaCardWeek;
  template: SadhanaTemplate;
  entries: SadhanaEntry[];
}

export interface SadhanaEntryUpsertRequest {
  itemId: number;
  entryDate: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueMinutes?: number | null;
  valueBool?: boolean | null;
  valueTime?: string | null;
  valueRounds?: number | null;
  completedTime?: string | null;
  notes?: string | null;
}

export interface SadhanaEntryMutationResponse {
  week: SadhanaCardWeek;
  entry: SadhanaEntry;
}

export interface SadhanaMentorCommentRequest {
  comment: string | null;
}

export interface ChantingPrescription {
  id: number;
  devoteeMemberId: number;
  mentorMemberId?: number | null;
  prescribedRounds: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  reason?: string | null;
  isException: boolean;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChantingPrescriptionCreateRequest {
  prescribedRounds: number;
  applyStrategy: SadhanaTemplateApplyStrategy;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  isException: boolean;
  reason?: string | null;
}

export interface ChantingPrescriptionsResponse {
  prescriptions: ChantingPrescription[];
}

export interface ChantingPrescriptionResponse {
  prescription: ChantingPrescription;
  autoRevert?: ChantingPrescription | null;
}

export interface SadhanaTemplateAssignmentCreateResponse {
  assignment: SadhanaTemplateAssignment;
  template: SadhanaTemplate;
}

export interface SadhanaChantingMemberAnalytics {
  devoteeMemberId: number;
  devoteeName: string;
  weeksTracked: number;
  complianceRate: number;
  roundsCompletedTotal: number;
  prescribedRoundsTotal: number;
}

export interface SadhanaChantingCohortAnalytics {
  mentorMemberId?: number | null;
  mentorName?: string | null;
  devoteeCount: number;
  averageCompliance: number;
  members: SadhanaChantingMemberAnalytics[];
}

export interface SadhanaChantingAnalyticsReport {
  generatedAt: string;
  rangeStart: string;
  rangeEnd: string;
  cohorts: SadhanaChantingCohortAnalytics[];
}

export interface SadhanaChantingAnalyticsResponse {
  report: SadhanaChantingAnalyticsReport;
}

export interface CongregationContactPoint {
  id: number;
  memberId?: number;
  type: string;
  value: string;
  isPrimary?: boolean;
  verifiedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationContactInput {
  type: string;
  value: string;
  isPrimary?: boolean;
}

export interface CongregationContactsResponse {
  contacts: CongregationContactPoint[];
}

export interface CongregationAddress {
  id: number;
  memberId?: number;
  kind: string;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationAddressInput {
  kind: string;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

export interface CongregationAddressesResponse {
  addresses: CongregationAddress[];
}

export type CongregationFamilyStatus =
  | 'PENDING_CONFIRMATION'
  | 'CONFIRMED'
  | 'REJECTED'
  | 'CANCELLED'
  | string;

export interface CongregationFamilyRelation {
  id: number;
  memberId: number;
  relatedMemberId: number;
  relation: string;
  status: CongregationFamilyStatus;
  visibility?: string | null;
  sinceDate?: string | null;
  createdBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
  relatedMemberDisplayName?: string | null;
  createdByDisplayName?: string | null;
  relatedMemberPhotoUrl?: string | null;
  createdByPhotoUrl?: string | null;
}

export interface CongregationManualRelative {
  id: number;
  memberId: number;
  name: string;
  relation: string;
  dateOfBirth?: string | null;
  phone?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  sinceDate?: string | null;
  notes?: string | null;
  visibility: string;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationManualRelativeInput {
  name: string;
  relation: string;
  dateOfBirth?: string | null;
  phone?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  sinceDate?: string | null;
  notes?: string | null;
  visibility?: string | null;
}

export interface CongregationManualRelativesResponse {
  relatives: CongregationManualRelative[];
}

export interface CongregationFamilyCreateRequest {
  relatedMemberId: number;
  relation: string;
  sinceDate?: string | null;
  visibility?: string | null;
}

export interface CongregationFamilyVisibilityInput {
  visibility: string;
}

export interface CongregationFamilyResponse {
  family: CongregationFamilyRelation[];
}

export type CongregationHouseholdVisibility = 'FAMILY' | 'TEMPLE' | string;

export interface CongregationHouseholdLabel {
  id: number;
  name: string;
  color?: string | null;
  description?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationHousehold {
  id: number;
  householdCode: string;
  name: string;
  visibility?: CongregationHouseholdVisibility | null;
  note?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
  headMemberId?: number | null;
  headMemberName?: string | null;
  memberCount?: number | null;
  exportRestricted?: boolean | null;
  exportRestrictedReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
  labels?: CongregationHouseholdLabel[];
  members?: CongregationHouseholdMember[];
}

export interface CongregationHouseholdMember {
  id: number;
  householdId: number;
  memberId: number;
  memberDisplayName: string;
  memberCenterName?: string | null;
  mentorDisplayName?: string | null;
  role: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  addedBy?: number | null;
  addedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationHouseholdsResponse {
  households: CongregationHousehold[];
}

export interface CongregationHouseholdResponse {
  household: CongregationHousehold;
}

export interface CongregationChantingRecord {
  id: number;
  memberId?: number;
  rounds: number;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationChantingInput {
  rounds: number;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
}

export interface CongregationChantingResponse {
  chanting: CongregationChantingRecord[];
}

export interface CongregationBookProgress {
  id: number;
  memberId?: number;
  bookId: number;
  status: string;
  completedOn?: string | null;
  remarks?: string | null;
  createdAt?: string;
  updatedAt?: string;
  bookTitle?: string | null;
}

export interface CongregationBookProgressInput {
  bookId: number;
  status: string;
  completedOn?: string | null;
  remarks?: string | null;
}

export interface CongregationBooksResponse {
  books: CongregationBookProgress[];
}

export interface CongregationCourseProgress {
  id: number;
  memberId?: number;
  courseId: number;
  status: string;
  yearCompleted?: number | null;
  verified?: boolean;
  verifierId?: number | null;
  verifiedOn?: string | null;
  createdAt?: string;
  updatedAt?: string;
  courseName?: string | null;
}

export interface CongregationCourseProgressInput {
  courseId: number;
  status: string;
  yearCompleted?: number | null;
}

export interface CongregationCoursesResponse {
  courses: CongregationCourseProgress[];
}

export interface CongregationMilestone {
  id: number;
  memberId?: number;
  type: string;
  initiatedName?: string | null;
  masterId?: number | null;
  date?: string | null;
  place?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationMilestoneInput {
  type: string;
  initiatedName?: string | null;
  masterId?: number | null;
  date?: string | null;
  place?: string | null;
  notes?: string | null;
}

export interface CongregationMilestonesResponse {
  milestones: CongregationMilestone[];
}

export interface CongregationDocument {
  id: number;
  memberId: number;
  docType: string;
  description?: string | null;
  fileUrl?: string | null;
  verified?: boolean;
  verifierId?: number | null;
  verifiedOn?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationDocumentInput {
  docType: string;
  description?: string | null;
  fileName?: string | null;
  fileUrl?: string | null;
  content?: string | null;
}

export interface CongregationDocumentsResponse {
  documents: CongregationDocument[];
}

export interface CongregationBirthdayReportEntry {
  memberId: number;
  displayName: string;
  dateOfBirth?: string | null;
  nextBirthday?: string | null;
  daysUntil?: number | null;
  ageTurning?: number | null;
}

export interface CongregationBirthdayReportResponse {
  birthdays: CongregationBirthdayReportEntry[];
}

export interface CongregationAnniversaryReportEntry {
  relationId: number;
  memberId: number;
  memberName: string;
  spouseId?: number | null;
  spouseName?: string | null;
  sinceDate?: string | null;
  nextAnniversary?: string | null;
  daysUntil?: number | null;
  yearsCelebrated?: number | null;
}

export interface CongregationAnniversaryReportResponse {
  anniversaries: CongregationAnniversaryReportEntry[];
}

export interface CongregationPhoto {
  id: number;
  memberId: number;
  kind: string;
  fileUrl: string;
  isCover?: boolean;
  uploadedBy?: number | null;
  uploadedAt?: string;
}

export interface CongregationPhotoInput {
  kind: string;
  fileName: string;
  content: string;
}

export interface CongregationPhotosResponse {
  photos: CongregationPhoto[];
}

export interface CongregationMemberStats {
  activeServicesCount: number;
  avgSadhanaScoreLast4Weeks: number;
  attendanceLast4Weeks: number;
  attendanceLast4WeeksLog: string[];
  sadhanaLoggedLast2Weeks: boolean;
}

export interface CongregationProfileSnapshot {
  member: CongregationMember;
  contacts: CongregationContactPoint[];
  addresses: CongregationAddress[];
  family: CongregationFamilyRelation[];
  chanting: CongregationChantingRecord[];
  books: CongregationBookReading[];
  courses: CongregationCourseProgress[];
  milestones: CongregationMilestone[];
  documents: CongregationDocument[];
  photos: CongregationPhoto[];
  stats: CongregationMemberStats;
}

export interface CongregationApplicationEvent {
  id: number;
  type: string;
  createdAt: string;
  actorRole?: string | null;
  actorName?: string | null;
  comment?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CongregationApplicationPersonalInput {
  displayName: string;
  legalName?: string | null;
  phone?: string | null;
  email?: string | null;
  pan?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  maritalStatus?: string | null;
  occupation?: string | null;
  education?: string | null;
  spiritualMasterId?: number | null;
  initiatedWith?: CongregationInitiatedWith;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  whatsappOptIn: boolean;
  about?: string | null;
}

export interface CongregationApplicationFamilyInput {
  maritalStatus?: string | null;
  dependents?: string[];
}

export interface CongregationApplicationDevotionalInput {
  chantingRounds?: number | null;
  dailySchedule?: string | null;
  services?: string[];
}

export interface CongregationApplicationSubmitRequest {
  personal: CongregationApplicationPersonalInput;
  family?: CongregationApplicationFamilyInput;
  devotional: CongregationApplicationDevotionalInput;
  preferredMentorId?: number | null;
  notes?: string | null;
}

export type CongregationApplicationSubmitPayload = CongregationApplicationSubmitRequest;

export interface CongregationMentorDecisionInput {
  comment: string;
}

export interface CongregationMentorRecommendationInput extends CongregationMentorDecisionInput {
  recommendedMentorId: number;
}

export interface CongregationMemberPersonalInput {
  displayName: string;
  legalName?: string | null;
  phone?: string | null;
  email?: string | null;
  pan?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  maritalStatus?: string | null;
  occupation?: string | null;
  education?: string | null;
  spiritualMasterId?: number | null;
  initiatedWith?: CongregationInitiatedWith;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  whatsappOptIn: boolean;
  about?: string | null;
}

export interface CongregationMentorDevoteeCreatePayload {
  member: CongregationMemberPersonalInput;
  preferredMentorId?: number | null;
  mentorComment: string;
}

export interface PhoneCheckResult {
  exists: boolean;
  status: 'new_account' | 'existing_user' | 'already_active' | 'pending_application';
  phone: string;
  userId?: number;
  memberId?: number;
  legalName?: string;
  email?: string;
  membershipStatus?: string;
  applicationId?: number;
  applicationStatus?: string;
}

export interface CongregationApplicationSummary {
  id: number;
  applicantId: number;
  applicantName?: string | null;
  preferredMentorId?: number | null;
  assignedMentorId?: number | null;
  assignedMentorName?: string | null;
  status: string;
  submittedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  comment?: string | null;
}

export interface CongregationApplication extends CongregationApplicationSummary {
  personal?: CongregationApplicationPersonalInput;
  family?: CongregationApplicationFamilyInput;
  devotional?: CongregationApplicationDevotionalInput;
  notes?: string | null;
  events?: CongregationApplicationEvent[];
  // Admin-portal compat fields
  applicantMemberId?: number | null;
  applicantDisplayName?: string | null;
  formData?: Record<string, unknown> | null;
}

export interface CongregationApplicationApi extends CongregationApplicationSummary {
  personal?: CongregationApplicationPersonalInput;
  family?: CongregationApplicationFamilyInput;
  devotional?: CongregationApplicationDevotionalInput;
  notes?: string | null;
  events?: CongregationApplicationEvent[];
}

export interface CongregationApplicationsApiResponse {
  applications: CongregationApplicationApi[];
}

export interface CongregationApplicationsResponse {
  applications: CongregationApplication[];
}

export interface CongregationMentorApplicationsResponse {
  applications: CongregationApplication[];
}

export type CongregationPersona = 'APPLICANT' | 'DEVOTEE' | 'MENTOR' | 'ADMIN';

export interface CongregationPersonaOption {
  key: CongregationPersona;
  label: string;
  description?: string;
}

export const congregationPersonaLabels: Record<CongregationPersona, string> = {
  APPLICANT: 'Application',
  DEVOTEE: 'Devotee workspace',
  MENTOR: 'Mentor workspace',
  ADMIN: 'Admin panel',
};

export const congregationPersonaRoutes: Record<CongregationPersona, string> = {
  APPLICANT: '/congregation/apply',
  DEVOTEE: '/congregation/devotee',
  MENTOR: '/congregation/mentor/overview',
  ADMIN: '/congregation/admin/devotees',
};

// Batch Attendance Types
export type BatchAttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'EXCUSED';

export interface BatchAttendanceRecord {
  recordId: number;
  attendanceId: number;
  memberId: number;
  memberName?: string;
  status: BatchAttendanceStatus;
  notes?: string;
  markedAt: string;
  batchName?: string;
  sessionTopic?: string;
}

export interface BatchAttendance {
  attendanceId: number;
  batchId: number;
  batchName?: string;
  sessionDate: string;
  sessionTopic?: string;
  notes?: string;
  createdBy: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  totalPresent?: number;
  totalAbsent?: number;
  totalLate?: number;
  totalExcused?: number;
}

export interface BatchAttendanceWithRecords extends BatchAttendance {
  records: BatchAttendanceRecord[];
}

export interface BatchAttendanceInput {
  sessionDate: string;
  sessionTopic?: string;
  notes?: string;
  records: Array<{
    memberId: number;
    status: BatchAttendanceStatus;
    notes?: string;
  }>;
}

// Services
export type CongregationServiceStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export interface CongregationServiceType {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationService {
  serviceId: number;
  memberId: number;
  memberName?: string;
  serviceTypeId: number;
  serviceType: string;
  assignedBy: number;
  assignedByName?: string;
  startDate: string;
  endDate?: string | null;
  description?: string;
  status: CongregationServiceStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationServiceInput {
  serviceTypeId: number;
  memberId: number;
  startDate: string;
  endDate?: string | null;
  description?: string;
  notes?: string;
  status?: CongregationServiceStatus;
}

// ---------------------------------------------------------------------------
// Admin portal compatibility types (used by ported congregation admin pages)
// ---------------------------------------------------------------------------

/** Alias compatible with admin portal's CongregationMembership */
export interface CongregationMembership {
  member: CongregationMember;
  roles: CongregationRoleAssignment[];
}

export type CongregationAddressKind = 'PERMANENT' | 'PRESENT' | 'OTHER';

export interface CongregationMemberAddress {
  id: number;
  memberId: number;
  kind: CongregationAddressKind;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export type CongregationBookStatus = 'READ' | 'READING' | 'NOT_STARTED';

export interface CongregationMemberBook {
  id: number;
  memberId: number;
  bookId: number;
  status: CongregationBookStatus;
  completedOn?: string | null;
  remarks?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CongregationCourseStatus = 'COMPLETED' | 'IN_PROGRESS' | 'NOT_STARTED';

export interface CongregationMemberCourse {
  id: number;
  memberId: number;
  courseId: number;
  status: CongregationCourseStatus;
  yearCompleted?: number | null;
  verified?: boolean;
  verifierId?: number | null;
  verifiedOn?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CongregationMilestoneType = 'HARINAM' | 'BRAHMIN' | 'SANNYAS';

export interface CongregationDevotionalMilestone {
  id: number;
  memberId: number;
  type: CongregationMilestoneType;
  initiatedName?: string | null;
  masterId?: number | null;
  date?: string | null;
  place?: string | null;
  ceremony?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CongregationDocumentType = string;

export interface CongregationMemberDocument {
  id: number;
  memberId: number;
  docType: CongregationDocumentType;
  description?: string | null;
  fileUrl: string;
  verified?: boolean;
  verifierId?: number | null;
  verifiedOn?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CongregationPhotoKind = 'PROFILE' | 'INITIATION' | 'OTHER';

export interface CongregationMemberPhoto {
  id: number;
  memberId: number;
  kind: CongregationPhotoKind;
  fileUrl: string;
  isCover?: boolean;
  uploadedBy?: number | null;
  uploadedAt: string;
}

export interface CongregationMemberProfile {
  member: CongregationMember;
  contacts: CongregationContactPoint[];
  addresses: CongregationMemberAddress[];
  family: CongregationFamilyRelation[];
  chanting: CongregationChantingRecord[];
  books: CongregationMemberBook[];
  courses: CongregationMemberCourse[];
  milestones: CongregationDevotionalMilestone[];
  documents: CongregationMemberDocument[];
  photos: CongregationMemberPhoto[];
}

// ---------------------------------------------------------------------------
// Admin input / response types (used by ported congregation admin pages)
// ---------------------------------------------------------------------------

export interface CongregationRoleAssignmentInput {
  memberId: number;
  role: CongregationRole;
  centerId?: number | null;
}

export interface CongregationCenterInput {
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export interface CongregationCourseInput {
  name: string;
  orderIndex?: number | null;
  description?: string | null;
  isRequired?: boolean;
}

export interface CongregationBookInput {
  title: string;
  author?: string | null;
  description?: string | null;
  orderIndex?: number | null;
  isPrimary?: boolean;
}

export interface CongregationSkillInput {
  name: string;
  description?: string | null;
}

export interface CongregationSpiritualMasterInput {
  name: string;
  centerId?: number | null;
  isActive?: boolean;
}

export interface CongregationSpiritualGuideInput {
  memberId: number;
  memberDisplayName: string;
  bio?: string | null;
  isActive?: boolean;
}

export interface CongregationCourseVerification {
  verified: boolean;
  verifierId?: number | null;
}

export interface CongregationDocumentVerification {
  verified: boolean;
  verifierId?: number | null;
}

export type CongregationApplicationStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'ASSIGNED_TO_MENTOR'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'WITHDRAWN'
  // Admin-portal compat statuses
  | 'INITIATED'
  | 'AWAITING_MENTOR_ASSIGNMENT'
  | 'MENTOR_ASSIGNED'
  | 'AWAITING_INTERVIEW'
  | 'INTERVIEW_SCHEDULED'
  | 'INTERVIEW_COMPLETED'
  | 'COMPLETED'
  | 'PENDING';

export interface CongregationApplicationPipeline {
  draft?: number;
  submitted?: number;
  assigned?: number;
  accepted?: number;
  rejected?: number;
  withdrawn?: number;
  pending?: number;
}

export interface CongregationAdminApplicationsResponse {
  applications: CongregationApplication[];
}

export interface CongregationAdminPipelineResponse {
  pipeline: CongregationApplicationPipeline;
}

export interface CongregationAdminActivityEvent {
  id: number;
  action: string;
  targetType: string;
  targetId?: number | string | null;
  targetLabel?: string | null;
  actorName?: string | null;
  actorRole?: CongregationRole | null;
  comment?: string | null;
  summary?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  createdAt: string;
}

export interface CongregationAdminActivityResponse {
  events: CongregationAdminActivityEvent[];
  nextCursor?: string | null;
}

export interface CongregationAdminAssignInput {
  mentorGuideId: number;
  comment?: string | null;
}

export type CongregationAdminOverrideStatus = 'ACCEPTED' | 'REJECTED';

export interface CongregationAdminOverrideInput {
  targetStatus: CongregationAdminOverrideStatus;
  comment: string;
}

export interface CongregationApplicationDetail {
  application: CongregationApplication;
  member?: CongregationMember | null;
  formData?: Record<string, unknown> | null;
  events?: CongregationApplicationEvent[] | null;
}

export interface CongregationMentorDevoteeCreate {
  member: CongregationMemberPersonalInput;
  mentorComment: string;
  preferredMentorId?: number | null;
}

export interface CongregationBirthdayReport {
  memberId: number;
  displayName: string;
  centerId?: number | null;
  centerName?: string | null;
  dateOfBirth?: string | null;
  nextBirthday?: string | null;
  daysUntil?: number | null;
  ageTurning?: number | null;
}

export interface CongregationAnniversaryReport {
  relationId: number;
  memberId: number;
  memberName: string;
  spouseId: number;
  spouseName: string;
  centerId?: number | null;
  centerName?: string | null;
  sinceDate?: string | null;
  nextAnniversary?: string | null;
  daysUntil?: number | null;
  yearsCelebrated?: number | null;
}

export interface CongregationHouseholdInput {
  name: string;
  visibility: CongregationHouseholdVisibility;
  note?: string | null;
}

export interface CongregationHouseholdMemberInput {
  memberId: number;
  role: string;
  effectiveFrom?: string | null;
}

export interface CongregationHouseholdMemberUpdateInput {
  role?: string;
  effectiveTo?: string | null;
}

export interface CongregationHouseholdHeadInput {
  memberId: number;
  justification?: string;
}

export interface CongregationHouseholdLabelInput {
  name: string;
  color?: string | null;
  description?: string | null;
  isActive?: boolean | null;
}

export interface CongregationHouseholdLabelAssignmentInput {
  labelIds: number[];
}

export interface CongregationHouseholdRosterEntry {
  household: CongregationHousehold;
  members: CongregationHouseholdMember[];
}

export interface CongregationEligibilityRule {
  id: number;
  name: string;
  description?: string | null;
  requiredRounds?: number | null;
  minimumCompletedCourses?: number | null;
  minimumCompletedBooks?: number | null;
  requireVerifiedCourses: boolean;
  requireVerifiedDocuments: boolean;
  requireHarinam: boolean;
  requireBrahmin: boolean;
  requiredCourseIds: number[];
  requiredBookIds: number[];
  requiredDocumentTypes: string[];
  isActive: boolean;
  createdBy: number;
  updatedBy?: number | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CongregationEligibilityRuleInput {
  name: string;
  description?: string | null;
  requiredRounds?: number | null;
  minimumCompletedCourses?: number | null;
  minimumCompletedBooks?: number | null;
  requireVerifiedCourses?: boolean | null;
  requireVerifiedDocuments?: boolean | null;
  requireHarinam?: boolean | null;
  requireBrahmin?: boolean | null;
  requiredCourseIds?: number[];
  requiredBookIds?: number[];
  requiredDocumentTypes?: string[];
  isActive?: boolean | null;
}

export interface CongregationEligibilityEvaluation {
  ruleId: number;
  memberId: number;
  status: string;
  completedRounds?: number | null;
  requiredRounds?: number | null;
  completedCourses: number[];
  requiredCourseIds: number[];
  completedBooks: number[];
  requiredBookIds: number[];
  hasHarinam: boolean;
  requireHarinam: boolean;
  hasBrahmin: boolean;
  requireBrahmin: boolean;
  verifiedDocumentTypes: string[];
  requiredDocumentTypes: string[];
  missingRequirements: string[];
  evaluatedAt: string;
}

export interface CongregationEligibilitySummary {
  ruleId: number;
  ruleName: string;
  eligibleCount: number;
  incompleteCount: number;
  totalMembers: number;
}

export interface CongregationMentorCohortSummary {
  mentorMemberId: number;
  mentorDisplayName: string;
  centerId?: number | null;
  centerName?: string | null;
  activeDevotees: number;
  pendingApplications: number;
  recentlyAccepted: number;
}

export interface CongregationChantingProgressRow {
  bucket: string;
  memberCount: number;
}

export interface CongregationCourseHeatmapRow {
  courseId: number;
  courseName: string;
  provider?: string | null;
  completedCount: number;
  inProgressCount: number;
  notStartedCount: number;
}

export type CongregationImportType =
  | 'MEMBER_CONTACTS'
  | 'CHANTING_RECORDS'
  | 'COURSE_COMPLETIONS'
  | 'BOOK_PROGRESS';

export type CongregationImportStatus =
  | 'QUEUED'
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface CongregationImportJob {
  id: number;
  importType: CongregationImportType | string;
  dryRun: boolean;
  status: CongregationImportStatus | string;
  totalRows?: number | null;
  processedRows?: number | null;
  successCount?: number | null;
  errorCount?: number | null;
  options?: Record<string, unknown> | null;
  summary?: Record<string, unknown> | null;
  createdByUserId?: number | null;
  createdByMemberId?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationImportRequest {
  importType: CongregationImportType;
  dryRun?: boolean;
  options?: Record<string, unknown> | null;
  rows: Record<string, unknown>[];
}

export type CongregationImportRowStatus = 'PENDING' | 'SUCCESS' | 'ERROR' | 'SKIPPED' | string;

export interface CongregationImportJobRow {
  id: number;
  jobId: number;
  rowNumber: number;
  rawData?: Record<string, unknown> | null;
  normalizedData?: Record<string, unknown> | null;
  status: CongregationImportRowStatus;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CongregationBackupStatus =
  | 'SCHEDULED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface CongregationBackupRun {
  id: number;
  requestedByUserId?: number | null;
  requestedByMemberId?: number | null;
  storageUri?: string | null;
  targetPointInTime?: string | null;
  status: CongregationBackupStatus;
  notes?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationBackupRequest {
  storageUri: string;
  targetPointInTime?: string | null;
  status: CongregationBackupStatus;
  notes?: string | null;
}

export interface CongregationBackupUpdate {
  status: CongregationBackupStatus;
  storageUri?: string | null;
  notes?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

export type CongregationAlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type CongregationAlertStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';

export interface CongregationMonitoringAlert {
  id: number;
  category: string;
  severity: CongregationAlertSeverity | string;
  message: string;
  details?: Record<string, unknown> | null;
  status: CongregationAlertStatus | string;
  triggeredAt: string;
  resolvedAt?: string | null;
  resolvedBy?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CongregationAlertRequest {
  category: string;
  severity: CongregationAlertSeverity | string;
  message: string;
  details?: Record<string, unknown> | null;
  status: CongregationAlertStatus;
}

export interface CongregationAccessReview {
  id: number;
  reviewerUserId?: number | null;
  reviewerMemberId?: number | null;
  reviewScope: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  totalMembers?: number | null;
  flaggedMembers?: number | null;
  notes?: string | null;
  attestedRoles?: CongregationRole[] | null;
  summary?: Record<string, unknown> | null;
  createdAt: string;
}

export interface CongregationAccessReviewRequest {
  reviewScope: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  notes?: string | null;
  attestedRoles: CongregationRole[];
}

// --- Member search (used by ported admin pages) ---

export interface CongregationMemberSearchMentorSummary {
  devoteeMemberId: number;
  mentorMemberId?: number | null;
  mentorName?: string | null;
}

export interface CongregationMemberSearchResult {
  member: CongregationMember;
  mentorSummary?: CongregationMemberSearchMentorSummary | null;
}
